import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  LocalPostOffice as PostaIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { format } from 'date-fns';
import { invoicesApi } from '../../services/api';
import { formatCustomerName } from '../../utils/formatCustomerName';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
};

const ExportPdfDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Filtri
  const [dataEmissioneDa, setDataEmissioneDa] = useState(null);
  const [dataEmissioneA, setDataEmissioneA] = useState(null);
  const [nufFrom, setNufFrom] = useState('');
  const [nufTo, setNufTo] = useState('');
  const [sendType, setSendType] = useState('all');
  
  // Dati
  const [invoices, setInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Export
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Carica anteprima fatture
  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const params = {};
      
      if (dataEmissioneDa) {
        params.from = format(dataEmissioneDa, 'yyyy-MM-dd');
      }
      if (dataEmissioneA) {
        params.to = format(dataEmissioneA, 'yyyy-MM-dd');
      }
      if (nufFrom) {
        params.nufFrom = parseInt(nufFrom);
      }
      if (nufTo) {
        params.nufTo = parseInt(nufTo);
      }
      
      if (!dataEmissioneDa && !dataEmissioneA && !nufFrom && !nufTo) {
        setError('Specifica almeno un filtro (date o numeri fattura)');
        setLoading(false);
        return;
      }
      
      const response = await invoicesApi.getAll({
        ...params,
        limit: 500,
        page: 1,
      });
      
      let fetchedInvoices = response.data?.data || [];
      
      // Filtro per tipo invio (allineato con logica invio email massivo)
      // Email: cliente deve avere email E invio_email abilitato
      // Posta: cliente senza email OPPURE invio_email non abilitato
      if (sendType !== 'all') {
        fetchedInvoices = fetchedInvoices.filter(inv => {
          const customer = inv.customer || {};
          const hasEmail = !!customer.email;
          const hasOptIn = customer.invio_email === true || customer.invio_email === 1;
          
          if (sendType === 'email') {
            // Solo se ha email E ha opt-in
            return hasEmail && hasOptIn;
          } else if (sendType === 'posta') {
            // Posta se non ha email OPPURE non ha opt-in
            return !hasEmail || !hasOptIn;
          }
          return true;
        });
      }
      
      fetchedInvoices = fetchedInvoices.filter(inv => inv.stato !== 'annullata');
      
      setInvoices(fetchedInvoices);
      setSelectedIds(fetchedInvoices.map(inv => inv.id));
      
    } catch (e) {
      setError(e.response?.data?.message || 'Errore nel caricamento delle fatture');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [dataEmissioneDa, dataEmissioneA, nufFrom, nufTo, sendType]);

  const handleExportSelected = async () => {
    if (selectedIds.length === 0) {
      setError('Nessuna fattura selezionata');
      return;
    }
    
    if (selectedIds.length > 200) {
      setError(`Troppi documenti selezionati (${selectedIds.length}). Massimo 200 fatture per export.`);
      return;
    }
    
    setExporting(true);
    setError(null);
    setSuccess(null);
    setExportProgress(10);
    
    try {
      // Passa direttamente gli ID delle fatture selezionate
      const filters = {
        invoiceIds: selectedIds,
      };
      
      if (dataEmissioneDa) {
        filters.dateFrom = format(dataEmissioneDa, 'yyyy-MM-dd');
      }
      if (dataEmissioneA) {
        filters.dateTo = format(dataEmissioneA, 'yyyy-MM-dd');
      }
      if (sendType !== 'all') {
        filters.sendType = sendType;
      }
      
      setExportProgress(30);
      
      const response = await invoicesApi.exportPdfBatch(filters);
      
      setExportProgress(80);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm');
      link.setAttribute('download', `fatture_export_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExportProgress(100);
      setSuccess(`Export completato! ${selectedIds.length} fatture scaricate.`);
      
    } catch (err) {
      console.error('Errore export PDF:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Errore nell\'export';
      setError(errorMessage);
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      setError(null);
      setSuccess(null);
      setInvoices([]);
      setSelectedIds([]);
      setDataEmissioneDa(null);
      setDataEmissioneA(null);
      setNufFrom('');
      setNufTo('');
      setSendType('all');
      onClose();
    }
  };

  const columns = [
    {
      field: 'nuf',
      headerName: 'N Fattura',
      width: 100,
      renderCell: (params) => (
        <Typography fontWeight="medium">{params.value}</Typography>
      ),
    },
    {
      field: 'customer',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => formatCustomerName(params.row.customer, params.row.f_nome || 'N/D'),
    },
    {
      field: 'f_emission',
      headerName: 'Emissione',
      width: 110,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'f_totale',
      headerName: 'Importo',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'saldo_residuo',
      headerName: 'Saldo',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const saldo = parseFloat(params.value) || 0;
        return (
          <Typography color={saldo > 0 ? 'error.main' : 'success.main'} fontWeight="medium">
            {formatCurrency(saldo)}
          </Typography>
        );
      },
    },
    {
      field: 'invio_type',
      headerName: 'Tipo Invio',
      width: 100,
      renderCell: (params) => {
        const customer = params.row.customer || {};
        const hasEmail = !!customer.email;
        const hasOptIn = customer.invio_email === true || customer.invio_email === 1;
        // Email solo se ha email E ha opt-in (stessa logica invio massivo)
        if (hasEmail && hasOptIn) {
          return <Chip icon={<EmailIcon />} label="Email" size="small" color="primary" variant="outlined" />;
        }
        return <Chip icon={<PostaIcon />} label="Posta" size="small" color="default" variant="outlined" />;
      },
    },
    {
      field: 'stato',
      headerName: 'Stato',
      width: 110,
      renderCell: (params) => {
        const saldoResiduo = parseFloat(params.row.saldo_residuo) || 0;
        const statoDb = params.value;
        
        if (statoDb === 'annullata') {
          return <Chip label="Annullata" size="small" color="default" />;
        }
        
        let effectiveStatus;
        if (saldoResiduo <= 0.01) {
          effectiveStatus = 'pagata';
        } else if (statoDb === 'parziale' || (parseFloat(params.row.f_paga_imp) || 0) > 0) {
          effectiveStatus = 'parziale';
        } else {
          effectiveStatus = 'emessa';
        }
        
        const statusMap = {
          'emessa': { label: 'Da Pagare', color: 'warning' },
          'parziale': { label: 'Parziale', color: 'info' },
          'pagata': { label: 'Pagata', color: 'success' },
        };
        const config = statusMap[effectiveStatus] || statusMap.emessa;
        return <Chip label={config.label} size="small" color={config.color} />;
      },
    },
  ];

  const selectedInvoices = invoices.filter(inv => selectedIds.includes(inv.id));
  const selectedTotal = selectedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.f_totale) || 0), 0);
  // Email solo se ha email E ha opt-in (stessa logica invio massivo)
  const emailCount = selectedInvoices.filter(inv => {
    const customer = inv.customer || {};
    const hasEmail = !!customer.email;
    const hasOptIn = customer.invio_email === true || customer.invio_email === 1;
    return hasEmail && hasOptIn;
  }).length;
  const postaCount = selectedInvoices.length - emailCount;

  return (
    <Dialog 
      open={open} 
      onClose={exporting ? undefined : handleClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { minHeight: isMobile ? 'auto' : '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PdfIcon color="error" />
            <Typography variant="h6">Export PDF Massivo</Typography>
          </Box>
          {!exporting && (
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Filtri
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <DatePicker
                label="Data emissione da"
                value={dataEmissioneDa}
                onChange={setDataEmissioneDa}
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
              <DatePicker
                label="Data emissione a"
                value={dataEmissioneA}
                onChange={setDataEmissioneA}
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
              <TextField
                label="Da N fattura"
                type="number"
                size="small"
                value={nufFrom}
                onChange={(e) => setNufFrom(e.target.value)}
                sx={{ width: 120 }}
                inputProps={{ min: 1 }}
              />
              <TextField
                label="A N fattura"
                type="number"
                size="small"
                value={nufTo}
                onChange={(e) => setNufTo(e.target.value)}
                sx={{ width: 120 }}
                inputProps={{ min: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Tipo Invio</InputLabel>
                <Select
                  value={sendType}
                  onChange={(e) => setSendType(e.target.value)}
                  label="Tipo Invio"
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="email">Solo Email</MenuItem>
                  <MenuItem value="posta">Solo Posta</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={loadPreview}
                disabled={loading || exporting}
              >
                Cerca
              </Button>
            </Box>
          </LocalizationProvider>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {exporting && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">
                Generazione PDF in corso...
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={exportProgress} 
              sx={{ mb: 1, height: 8, borderRadius: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Attendere il completamento del download...
            </Typography>
          </Paper>
        )}

        {invoices.length > 0 && !exporting && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{invoices.length}</strong> fatture trovate - 
            <strong>{invoices.filter(inv => inv.customer?.invio_email).length}</strong> via email - 
            <strong>{invoices.filter(inv => !inv.customer?.invio_email).length}</strong> via posta - 
            Totale: <strong>{formatCurrency(invoices.reduce((sum, inv) => sum + (parseFloat(inv.f_totale) || 0), 0))}</strong>
          </Alert>
        )}

        {selectedIds.length > 0 && !exporting && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<DownloadIcon />}>
            <strong>{selectedIds.length}</strong> fatture selezionate - 
            <strong>{emailCount}</strong> email - 
            <strong>{postaCount}</strong> posta - 
            Importo totale: <strong>{formatCurrency(selectedTotal)}</strong>
          </Alert>
        )}

        <DataGrid
          rows={invoices}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
          autoHeight
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          sx={{ 
            border: 'none',
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={exporting}>
          Chiudi
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Button
          variant="contained"
          color="error"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleExportSelected}
          disabled={selectedIds.length === 0 || loading || exporting}
        >
          {exporting ? 'Generazione...' : `Esporta Selezionate (${selectedIds.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportPdfDialog;
